import { t } from "i18next";
import { useEffect, useState } from "react";
import { min } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import C from "Constants";
import ConditionalRenderer from "components/ConditionalRenderer";
import { useUserWalletState } from "hooks";
import { LoyaltyAction } from "store/actions";
import ApplyVoucher from "components/discounts-loyalty/ApplyVoucher";
import LoyaltyRedeemSwitch from "components/brand-loyalty/LoyaltyRedeemSwitch";
import LoyaltyCreditWidget from "components/brand-loyalty/LoyaltyCreditWidget";
import { DiscountAction } from "store/actions";
import CustomModal from "components/CustomModal";
import ConfirmModal from "components/Modals/ConfirmModal";
import {
  SALE_ITEMS_BLOCK_LOYALTY_MESSAGE,
  cartHasSalePricedItem,
} from "utils/discounts.utils";
// import { CartAction } from "store/actions";

const DiscountLoyaltyApply = ({
  isApplyVoucherOpen,
  setIsApplyVoucherOpen = () => {},
  updateCart = () => {},
}) => {
  const dispatch = useDispatch();

  const { cart, cartTotal, previousDiscountApplied } = useSelector(
    (state) => state.cart
  );
  const { discountInfo, isDiscountApplied } = useSelector(
    (state) => state?.Discounts
  );

  const { loyaltyApplied } = useSelector((state) => state?.Loyalty);
  const { loyaltyPolicy } = useSelector((state) => state.brandStorageReducer);

  const {
    balance: userWallet,
    isLoading: isWalletLoading,
    hasError: walletLoadFailed,
  } = useUserWalletState();

  const [redeemAmount, setRedeemAmount] = useState(0);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmationCallback, setConfirmationCallback] = useState(
    () => () => {}
  );
  const [confirmModalTitle, setConfirmModalTitle] = useState({
    title: "",
    message: "",
  });
  const [openDiscountPopup, setDiscountPopup] = useState(false);

  const switchLoyaltyStatus = () => {
    if (loyaltyApplied) {
      dispatch(LoyaltyAction.removeLoyalty());
      updateCart();
    } else {
      dispatch(LoyaltyAction.applyLoyalty(Number(redeemAmount)));
      updateCart();
    }
  };

  const handleConfirmDiscountLoyalty = (wantsToApply) => {
    if (loyaltyApplied && wantsToApply === C.DISCOUNT) {
      setConfirmModal(true);
      setConfirmModalTitle({
        title: t("Use discount code"),
        message: t(
          "To use discount code, the loyalty points will be removed. Do you want to continue"
        ),
      });

      setConfirmationCallback(() => () => {
        dispatch(LoyaltyAction.removeLoyalty());

      //TODO: uncomment this is we want to only apply the discount on the whole order and remove the discount from the cart items
        // dispatch(CartAction.removeDiscountAmountFromCartItems());
        setDiscountPopup(true);
        updateCart();
      });
    } else if (
      (isDiscountApplied || discountInfo) &&
      wantsToApply === C.LOYALTY
    ) {
      setConfirmModal(true);
      setConfirmModalTitle({
        title: t("Use loyalty credits"),
        message: t(
          "To use loyalty points, the discount code will be removed. Do you want to continue"
        ),
      });
      setConfirmationCallback(() => () => {
        dispatch(DiscountAction.setCoponValidation(false));
        dispatch(DiscountAction.removeDiscountCode());

      //TODO: uncomment this is we want to only apply the discount on the whole order and remove the discount from the cart items
        // dispatch(CartAction.removeDiscountAmountFromCartItems());
        switchLoyaltyStatus();
        updateCart();
      });
    } else if (wantsToApply === C.LOYALTY) {
      switchLoyaltyStatus();
    } else if (wantsToApply === C.DISCOUNT) {
      setDiscountPopup(true);
    }
  };

  const handleConfirmModal = () => {
    setConfirmModal(false);
    confirmationCallback();
  };

  const handleCancelModal = () => {
    setConfirmModal(false);
    setIsApplyVoucherOpen(false);
    setDiscountPopup(false);
  };

  const calculateRedeemAmount = () => {
    // The balance is one of the three inputs to the redeem amount. While it is
    // unknown, computing anything would produce a zero that is indistinguishable
    // from "no points available", and writing that zero to the store would drop
    // the redemption from an order placed in the meantime.
    if (isWalletLoading) {
      return;
    }

    const maxRedeemableValuePerOrder =
      loyaltyPolicy?.maxRedeemableValuePerOrder || 0;
    const redeemablePercentagePerOrder =
      loyaltyPolicy?.redeemablePercentagePerOrder || 0;
    const redeemableAmount = Number(
      (cartTotal / 100) * redeemablePercentagePerOrder
    );
    let minimum = min([
      maxRedeemableValuePerOrder,
      redeemableAmount,
      Number(userWallet) || 0,
    ])?.toFixed(2);
    setRedeemAmount(Number(minimum));
    updateCart();
    dispatch(LoyaltyAction.updateloyaltyAmount(Number(minimum)));
  };

  useEffect(() => {
    calculateRedeemAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartTotal, loyaltyPolicy, cart, userWallet, isWalletLoading]);

  // One discount at a time: a sale-priced item blocks loyalty redemption. If
  // loyalty was applied before a sale item reached the basket, drop it.
  const hasSalePricedItem = cartHasSalePricedItem(cart);
  useEffect(() => {
    if (hasSalePricedItem && loyaltyApplied) {
      dispatch(LoyaltyAction.removeLoyalty());
      updateCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSalePricedItem, loyaltyApplied]);

  useEffect(() => {
    if (isApplyVoucherOpen) {
      handleConfirmDiscountLoyalty(C.DISCOUNT);
    }
  }, [isApplyVoucherOpen]);

  // A voucher carried over from a previous order cannot be removed (the voucher
  // button and its remove control are both disabled), so loyalty genuinely
  // cannot be swapped in. An ordinary voucher can still be swapped through the
  // confirmation modal, so the toggle stays usable in that case.
  const isLockedVoucherApplied =
    Object?.keys?.(previousDiscountApplied || {})?.length > 0;

  const getUnavailableReason = () => {
    if (isWalletLoading) return "Checking your loyalty balance";
    if (walletLoadFailed) return "Could not load your loyalty balance";
    if (hasSalePricedItem) return SALE_ITEMS_BLOCK_LOYALTY_MESSAGE;
    if (isLockedVoucherApplied) {
      return "Remove the voucher to use your loyalty balance";
    }
    if (!(Number(userWallet) > 0)) {
      return "You have no loyalty balance to redeem";
    }
    if (!(Number(redeemAmount) > 0)) {
      return "Loyalty redemption is not available on this order";
    }
    return null;
  };

  return (
    <>
      <ConditionalRenderer condition={!!loyaltyPolicy?.isLoyaltyActive}>
        <LoyaltyRedeemSwitch
          switchLoyaltyStatus={() => handleConfirmDiscountLoyalty(C.LOYALTY)}
          redeemAmount={redeemAmount}
          unavailableReason={getUnavailableReason()}
          wrapperStyleProps={{
            className: "justify-between",
            style: {
              flexDirection: "row-reverse",
            },
          }}
          textStyleProps={{
            className: "flex gap-1 flex-wrap text-16x font-medium",
          }}
        />
        {loyaltyApplied && (
          <LoyaltyCreditWidget
            {...{ loyaltyPolicy }}
            containerStyleProps={{
              style: { margin: "0px" },
            }}
          />
        )}
      </ConditionalRenderer>
      <ApplyVoucher
        isOpen={openDiscountPopup}
        onClose={() => {
          setIsApplyVoucherOpen(false);
          setDiscountPopup(false);
        }}
      />
      <CustomModal
        open={confirmModal}
        setOpen={setConfirmModal ?? (() => {})}
        bodyclasses="flex"
        overlayClasses="fixed h-screen w-screen top-0 bg-opacity-50 overflow-hidden transition ease-linear duration-700 z-40 "
      >
        <ConfirmModal
          onConfirm={handleConfirmModal ?? (() => {})}
          onCancel={handleCancelModal ?? (() => {})}
          title={confirmModalTitle}
        />
      </CustomModal>
    </>
  );
};

export default DiscountLoyaltyApply;
